//
//
//
//
//
//
//
//
//
//
//
//
//

import { ResizeSensor } from 'css-element-queries';
import L from 'leaflet';
import _ from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { useListableProperties } from '@/api/legacy/useListableProperties';
import { Features, isEnabled } from '@/config/features';
import { displayDateTime } from '@/helpers/time';
import Convert from '@/mixins/convert'; // tmw_used
import { PALETTE_COLOURS } from '@/store/types/getters';
import { COLOURS, PERSISTENCE } from '@/store/types/namespaces';
import 'leaflet.markercluster';
import 'leaflet.heat';
import 'leaflet.gridlayer.googlemutant/dist/Leaflet.GoogleMutant';
import 'leaflet-polylinedecorator';
import 'leaflet-easybutton';
import { apiLoaded, load } from './googleMaps';
import { bulkGenerateMarkers, generateClusterMarker, locateDevice, locateDevices } from './helpers';
import { DEFAULT_STYLE, NEUTRAL_STYLE, NIGHT_MODE_NEUTRAL_STYLE, NIGHT_MODE_STYLE, NON_ROADS_STYLE, REDUCED_COLOUR_SCHEME, colourSchemeMap } from './styles';
import WeatherOverlay from './weather-overlay';

L.weatherOverlay = WeatherOverlay;

let buildResolver;

const Styles = {
  DEFAULT: DEFAULT_STYLE,
  NEUTRAL: NEUTRAL_STYLE,
  DARK: NIGHT_MODE_STYLE,
  DARK_NEUTRAL: NIGHT_MODE_NEUTRAL_STYLE,
};

const FIT_BOUNDS_MAX_ZOOM = 17;

// Add method to layer control class
L.Control.Layers.include({
  getActiveOverlayNames() {
    // Create array for holding active layers
    const active = [];

    // Iterate all layers in control
    this._layers.forEach((obj) => { // eslint-disable-line no-underscore-dangle
      // Check if it's an overlay and added to the map
      // eslint-disable-next-line no-underscore-dangle
      if (obj.overlay && this._map.hasLayer(obj.layer)) {
        // Push layer to active array
        active.push(obj.name);
      }
    });

    // Return array
    return active;
  },
});

export default {
  name: 'DpLeafletMap',
  mixins: [Convert],
  props: {
    devices: { type: Object, default: () => ({}) },
    settings: { type: Object, default: () => ({}) },
    selectionEnabled: { type: Boolean, default: false },
    selectedDevices: { type: Array, default: () => [] },
    initialLayer: { type: String, default: null },
    initialOverlays: { type: Array, default: () => [] },
    showLayerPicker: { type: Boolean, default: true },
    showBoundsLock: { type: Boolean, default: false },
    lockedBounds: { type: Object, default: null },
    kpi: { type: Object, default: null },
    timePeriod: { type: Object, default: null },
    weatherEnabled: { type: Boolean, default: false },
  },
  setup() {
    const { listableProperties } = useListableProperties();
    return { listableProperties };
  },
  data() {
    return {
      loadedColourScheme: null,
      tileLayers: {
        road: null,
        satellite: null,
        terrain: null,
        hybrid: null,
      },
      weatherLayer: null,
      layerControl: null,
      boundsLockButton: null,
      resizeSensor: null,
      buildPromise: new Promise((resolve) => {
        buildResolver = resolve;
      }),
      minimumColourValue: null,
      maximumColourValue: null,
      refreshFn: _.debounce(this.refresh, 500),
    };
  },
  computed: {
    ...mapGetters(COLOURS, [PALETTE_COLOURS]),
    ...mapState(PERSISTENCE, ['uiDark']),
    parsedLockedBounds() {
      if (this.lockedBounds && this.lockedBounds.ne && this.lockedBounds.ne.lat) {
        const { ne, sw } = this.lockedBounds;
        const corner1 = L.latLng(ne.lat, ne.lng);
        const corner2 = L.latLng(sw.lat, sw.lng);
        const bounds = L.latLngBounds(corner1, corner2);

        if (bounds.isValid()) {
          return bounds;
        }
      }
      return null;
    },
    legendColours() {
      const { colouring } = this.settings;
      if (colouring) {
        const { start, end } = colouring;
        if (!start || !end) return null;
        return [start, end];
      }
      return null;
    },
  },
  watch: {
    devices(n, o) {
      if (Object.keys(n).length === 0 && Object.keys(o).length === 0) return;
      this.refreshFn();
    },
    settings: {
      handler() {
        this.refreshFn();
      },
      deep: true,
    },
    selectedDevices() {
      const selectedMarkers = this.updateSelectedDeviceData();

      const group = new L.featureGroup(selectedMarkers); // eslint-disable-line new-cap
      const bounds = group.getBounds();

      if (bounds.isValid() && !this.map.getBounds().contains(bounds)) {
        this.fitBounds(bounds);
      }
    },
    showLayerPicker(newValue, oldValue) {
      if (!this.layerControl || newValue === oldValue) {
        return;
      }

      if (newValue === true) {
        this.layerControl.addTo(this.map);
      } else {
        this.layerControl.remove();
      }
    },
    showBoundsLock(isVisible) {
      if (!this.boundsLockButton) {
        return;
      }

      if (isVisible) {
        this.boundsLockButton.addTo(this.map);
      } else {
        this.boundsLockButton.remove();
      }
    },
    parsedLockedBounds(bounds) {
      if (!this.map) return;

      this.toggleMovement(bounds === null);
    },
  },
  created() {
    // Create these here to ensure they are not reactive
    this.map = null;
    this.markers = [];
    this.lastDevices = null;
    this.cluster = null;
    this.heatmap = null;
    this.drawingLayer = null;
    this.annotationLayer = null;
    this.radiusIndicator = null;

    load({
      libraries: ['visualization'],
    });
  },
  mounted() {
    this.build();
  },
  beforeDestroy() {
    if (!this.$el || !this.resizeSensor) {
      return;
    }
    this.resizeSensor.detach(this.$el);
    delete this.resizeSensor;

    // Clear up all map stuff implicitly, for performance reasons
    this.clear();
    this.markers.forEach((m, index) => {
      m.remove();
      delete this.markers[index];
    });

    if (this.heatmap) {
      this.map.removeLayer(this.heatmap);
    }
    if (this.cluster) {
      this.map.removeLayer(this.cluster);
    }
    if (this.drawingLayer) {
      this.map.removeLayer(this.drawingLayer);
    }
    if (this.annotationLayer) {
      this.map.removeLayer(this.annotationLayer);
    }
    if (this.boundsLockButton) {
      this.map.removeLayer(this.boundsLockButton);
    }

    this.map.off('baselayerchange');
    this.map.remove();
  },
  methods: {
    updateSelectedDeviceData() {
      const flatArray = this.selectedDevices.map((d) => d.$id);
      const selectedMarkers = [];
      this.markers.forEach((m) => {
        if (flatArray.indexOf(m.options.id) > -1) {
          selectedMarkers.push(m);
          m.options.isSelected = true; // eslint-disable-line no-param-reassign
        } else {
          m.options.isSelected = false; // eslint-disable-line no-param-reassign
        }
      });
      this.updateSelectedDeviceMarkers();
      return selectedMarkers;
    },
    toggleMovement(enabled) {
      const action = enabled ? 'enable' : 'disable';
      this.map.dragging[action]();
      this.map.touchZoom[action]();
      this.map.doubleClickZoom[action]();
      this.map.scrollWheelZoom[action]();

      if (enabled) {
        this.map.addControl(this.map.zoomControl);
        this.boundsLockButton.state('lock');
      } else {
        this.map.removeControl(this.map.zoomControl);
        this.boundsLockButton.state('unlock');
      }
    },
    build() {
      apiLoaded.then(() => {
        if (!this.$refs.container) return;
        this.map = L.map(this.$refs.container, {
          attributionControl: false,
          layers: [],
          worldCopyJump: true,
          dragging: !L.Browser.mobile,
          scrollWheelZoom: !L.Browser.mobile,
        });

        const styleName = this.getStyleName();
        this.loadedColourScheme = styleName;

        this.tileLayers = {
          Streets: L.gridLayer.googleMutant({
            type: 'roadmap',
            styles: Styles[styleName],
          }),
          Satellite: L.gridLayer.googleMutant({
            type: 'satellite',
            styles: NON_ROADS_STYLE,
          }),
          Terrain: L.gridLayer.googleMutant({
            type: 'terrain',
            styles: Styles[styleName],
          }),
          Hybrid: L.gridLayer.googleMutant({
            type: 'hybrid',
            styles: NON_ROADS_STYLE,
          }),
        };

        this.overlays = {};
        if (isEnabled(Features.XYLEM)) {
          const types = [
            { label: 'Cables', layer: 'kabel' },
            { label: 'Pipes', layer: 'leiding' },
            { label: 'Manhole', layer: 'manhole' },
            { label: 'Environmental management facility', layer: 'milieumanagementfaciliteit' },
            { label: 'Pipe', layer: 'pipe' },
          ];
          types.forEach(({ label, layer }) => {
            this.overlays[label] = L.tileLayer.wms('https://geodata.nationaalgeoregister.nl/wsdiensteninspire/wms/service?', {
              layers: layer,
              format: 'image/png',
              transparent: true,
            });
          });
        }

        this.layerControl = L.control.layers({
          Streets: this.tileLayers.Streets,
          Satellite: this.tileLayers.Satellite,
          Hybrid: this.tileLayers.Hybrid,
          Terrain: this.tileLayers.Terrain,
        }, this.overlays, { position: 'topleft' });

        if (isEnabled(Features.XYLEM)) {
          // this.layerControl.addOverlay({
          //   Test: L.tileLayer.wms('https://geodata.nationaalgeoregister.nl/wsdiensteninspire/wms/service?', {
          //     layers: 'kabal',
          //     format: 'image/png',
          //     transparent: true,
          //   }),
          // });
        }

        if (this.showLayerPicker) {
          this.layerControl.addTo(this.map);
        }

        const buttonEvent = (map) => {
          this.$emit('bounds-locked', map.getBounds());
        };

        this.boundsLockButton = L.easyButton({
          states: [{
            stateName: 'lock',
            icon: 'fa-unlock',
            title: 'Lock current view',
            onClick: (btn, map) => {
              btn.state('unlock');
              buttonEvent(map);
            },
          }, {
            stateName: 'unlock',
            icon: 'fa-lock',
            title: 'Zoom to fit markers',
            onClick: (btn, map) => {
              btn.state('lock');
              buttonEvent(map);
            },
          }],
        });

        if (this.showBoundsLock) {
          this.boundsLockButton.addTo(this.map);
        }

        // Add the starting layer
        if (this.initialLayer) {
          this.tileLayers[this.initialLayer].addTo(this.map);
        } else {
          this.tileLayers.Streets.addTo(this.map);
        }

        if (this.initialOverlays && Array.isArray(this.initialOverlays)) {
          this.initialOverlays.forEach((overlayName) => {
            if (this.overlays[overlayName]) {
              this.overlays[overlayName].addTo(this.map);
            }
          });
        }

        // The layer to hold any custom drawing
        this.drawingLayer = L.layerGroup().addTo(this.map);
        this.annotationLayer = L.layerGroup().addTo(this.map);

        // Listen to container resize events to prevent partial render:
        // https://github.com/Leaflet/Leaflet/issues/4835
        this.resizeSensor = new ResizeSensor(this.$el, _.debounce(this.handleResize, 500));
        buildResolver();

        this.$nextTick(() => {
          if (Object.keys(this.devices).length) {
            this.refreshFn();
          }

          this.map.on('baselayerchange', this.changedLayer);
          this.map.on('overlayadd', this.changedOverlays);
          this.map.on('overlayremove', this.changedOverlays);
          this.map.on('moveend', this.changedBounds);
        });

        if (this.parsedLockedBounds) {
          this.fitBounds();
          this.toggleMovement(false);
        }
      });
    },

    getStyleName() {
      let { colourScheme } = this.settings;

      if (!colourScheme) {
        return this.uiDark ? 'DARK' : 'DEFAULT';
      } if (typeof colourScheme === 'object') {
        colourScheme = colourScheme.name;
      }

      colourScheme = colourSchemeMap[colourScheme];

      if (this.uiDark && colourScheme === REDUCED_COLOUR_SCHEME) {
        return 'DARK_NEUTRAL';
      } if (this.uiDark) {
        return 'DARK';
      } if (colourScheme === REDUCED_COLOUR_SCHEME) {
        return 'NEUTRAL';
      }
      return 'DEFAULT';
    },

    updateLayers() {
      // Return early if map has not yet been built
      if (!this.map || !this.$refs.container) return;

      if (this.map.getZoom() === undefined) {
        // all layers need an initial zoom/centre
        this.map.setView([51.5, 0.12], 10);
      }

      const { useHeatmap, useWeatherOverlay } = this.settings;

      const styleName = this.getStyleName();
      if (styleName !== this.loadedColourScheme) {
        // Remove old styles
        this.map.removeLayer(this.tileLayers.Streets);
        this.map.removeLayer(this.tileLayers.Terrain);

        this.tileLayers.Streets = L.gridLayer.googleMutant({
          type: 'roadmap',
          styles: Styles[styleName],
        });
        this.tileLayers.Terrain = L.gridLayer.googleMutant({
          type: 'terrain',
          styles: Styles[styleName],
        });
        this.loadedColourScheme = styleName;

        // Add the starting layer
        if (this.initialLayer === 'Terrain') {
          this.tileLayers.Terrain.addTo(this.map);
        } else {
          this.tileLayers.Streets.addTo(this.map);
        }

        this.layerControl.remove();
        this.layerControl = L.control.layers({
          Streets: this.tileLayers.Streets,
          Satellite: this.tileLayers.Satellite,
          Hybrid: this.tileLayers.Hybrid,
          Terrain: this.tileLayers.Terrain,
        }, null, { position: 'topleft' }).addTo(this.map);
      }

      if (useHeatmap && !this.heatmap) {
        if (this.cluster) {
          this.map.removeLayer(this.cluster);
          this.cluster = undefined;
        }
        this.heatmap = L.heatLayer([], {
          minOpacity: 0.6,
          gradient: {
            0.65: 'blue',
            0.7: 'cyan',
            0.8: 'lime',
            0.9: 'yellow',
            1.0: 'red',
          },
        });
        this.map.addLayer(this.heatmap);
      }

      if (!useHeatmap) {
        if (this.heatmap) {
          this.map.removeLayer(this.heatmap);
          this.heatmap = undefined;
        }

        // If the cluster already exists, destroy it before recreating
        if (this.cluster) {
          this.cluster.clearLayers();
          this.map.removeLayer(this.cluster);
        }

        const clusterParams = {
          ...this.settings,
          $kpi: this.kpi ? this.kpi : null,
        };

        if (this.settings.colouring && this.settings.colouring.property) {
          clusterParams.colourProperty = typeof this.settings.colouring.property === 'object'
            ? this.convertPropertyNameToProperty(this.settings.colouring.property.name)
            : this.convertPropertyNameToProperty(this.settings.colouring.property);
        }

        this.cluster = new L.markerClusterGroup({ // eslint-disable-line new-cap
          chunkedLoading: true,
          maxClusterRadius: 40,
          showCoverageOnHover: true,
          removeOutsideVisibleBounds: true,
          iconCreateFunction: (group) => generateClusterMarker(group, clusterParams),
        });
        this.map.addLayer(this.cluster);
      }

      if (useWeatherOverlay) {
        if (this.weatherLayer) {
          this.map.removeLayer(this.weatherLayer);
        }

        const anchorTime = this.timeRange?.range?.anchor;
        const options = {};
        if (anchorTime) {
          options.anchorTime = anchorTime;
        }
        this.weatherLayer = new L.weatherOverlay(options); // eslint-disable-line new-cap
        this.map.addLayer(this.weatherLayer);
      }
    },

    renderHeatmap(locations) {
      // leaflet.heat has proven good, non-chunked, performance to 10k points.
      const latLngs = Object
        .values(locations)
        .map(({ longitude, latitude }) => [latitude, longitude]);
      this.heatmap.setLatLngs(latLngs);

      const opacity = (this.settings.heatmapOpacity || 100) / 100;
      const heatmapDom = L.DomUtil.get(this.heatmap);

      if (heatmapDom && heatmapDom._canvas) { // eslint-disable-line no-underscore-dangle
        L.DomUtil.setOpacity(heatmapDom._canvas, opacity); // eslint-disable-line no-underscore-dangle, max-len
      }

      if (latLngs.length === 0) {
        return Promise.resolve();
      }

      const bounds = new L.latLngBounds(this.heatmap._latlngs); // eslint-disable-line no-underscore-dangle, new-cap, max-len
      return Promise.resolve(bounds);
    },

    renderMarkers(locations) {
      // configure colour options:
      let minimumColourValue = Infinity;
      let maximumColourValue = -Infinity;
      let propertySetRange = false;

      let startColour = this[PALETTE_COLOURS].success;
      let endColour = this[PALETTE_COLOURS].error;
      if (this.settings.colouring && this.settings.colouring.start) {
        startColour = this.settings.colouring.start;
      }
      if (this.settings.colouring && this.settings.colouring.end) {
        endColour = this.settings.colouring.end;
      }
      let colourProperty = null;
      if (this.settings.colouring && this.settings.colouring.property) {
        colourProperty = typeof this.settings.colouring.property === 'object'
          ? this.convertPropertyNameToProperty(this.settings.colouring.property.name)
          : this.convertPropertyNameToProperty(this.settings.colouring.property);

        const range = colourProperty?.$property?.displayRange;
        if (range) {
          propertySetRange = true;
          minimumColourValue = range.min;
          maximumColourValue = range.max;
        }
      }

      // configure tooltip options:
      let tooltipProperty = { name: '$id', format: (p) => p };
      if (this.settings.tooltipProperty) {
        tooltipProperty = typeof this.settings.tooltipProperty === 'object'
          ? this.convertPropertyNameToProperty(this.settings.tooltipProperty.name)
          : this.convertPropertyNameToProperty(this.settings.tooltipProperty);
      }

      const tt = (p, device) => {
        const value = p.isTs ? displayDateTime(device[p.name]) : device[p.name];
        return `<b>${p.alias}:</b> ${value === undefined ? null : value}`;
      };

      const dataArray = Object.values(locations)
        .filter((loc) => (this.kpi && this.devices[loc.$id].$kpi !== undefined) || !this.kpi)
        .map(({ $id, latitude, longitude }) => {
          const device = this.devices[$id];
          let tooltip = device[tooltipProperty.name];

          const { tooltipProperties } = this.settings;
          if (tooltipProperties && tooltipProperties.length > 0) {
            tooltip = tooltipProperties.map((p) => tt(p, device)).join('<br>');
          } else {
            tooltip = device.$id;
          }

          let colour = null;
          if (colourProperty && !propertySetRange) {
            colour = device[colourProperty.name];
            if (!Number.isNaN(colour) && colour < minimumColourValue) {
              minimumColourValue = colour;
            }
            if (!Number.isNaN(colour) && colour > maximumColourValue) {
              maximumColourValue = colour;
            }
          }

          return {
            tooltip,
            device: { ...device, latitude, longitude },
            settings: this.settings,
          };
        });

      // Generic, non-device-specific styling arguments
      const args = {
        minimumColourValue,
        maximumColourValue,
        colourProperty,
        startColour,
        endColour,
        darkMode: this.uiDark,
        useColour: this.settings.useColour,
      };

      if (colourProperty && minimumColourValue < Infinity) {
        this.minimumColourValue = parseFloat(minimumColourValue);
      } else {
        this.minimumColourValue = null;
      }

      if (colourProperty && maximumColourValue > -Infinity) {
        this.maximumColourValue = parseFloat(maximumColourValue);
      } else {
        this.maxiumumColourValue = null;
      }

      if (this.kpi) {
        args.colourProperty = {
          name: '$kpi',
          alias: this.kpi.name,
          format: (v) => {
            if (v) {
              return v.value;
            }
            return null;
          },
        };
      }

      this.clear();

      return bulkGenerateMarkers(dataArray, args)
        .then((markers) => {
          // Escape if the map has been destroyed by the time this promise returns
          if (!this.map || !this.$refs.container) return Promise.resolve();

          this.markers = markers;

          const handleMarkerClick = (marker, e) => {
            if (marker.options.isSelected) {
              marker.options.isSelected = false; // eslint-disable-line no-param-reassign
            } else {
              marker.options.isSelected = true; // eslint-disable-line no-param-reassign
            }

            // If we are not multi-selecting, deselect other markers
            if (!e.originalEvent.metaKey) {
              this.markers = this.markers.map((m) => {
                const newMarker = m;
                if (m !== marker) {
                  newMarker.options.isSelected = false;
                }
                return newMarker;
              });
            }

            this.pushDeviceSelectionChange();
          };

          // Clustered markers
          this.markers.forEach((marker) => {
            // Add click listener if we enabled selection
            if (this.selectionEnabled) {
              marker.on('click', (e) => handleMarkerClick(marker, e));
            }

            // Tooltip has to be bound after adding to the map
            const t = `${marker.options.tooltip}`;
            if (t) { marker.bindTooltip(t); }
          });

          const chunked = new Promise((resolve) => {
            this.cluster.options.chunkProgress = (processed, total) => {
              if (processed === total) { resolve(); }
            };
          });

          this.cluster.clearLayers();
          if (this.markers.length === 0) { return Promise.resolve(); }

          this.cluster.addLayers(this.markers);
          return chunked;
        })
        .then(() => {
          if (!this.markers || this.markers.length === 0) {
            return Promise.resolve();
          }

          // If there are selected devices, zoom to their bounds
          if (this.selectedDevices.length > 0) {
            const selectedMarkers = this.updateSelectedDeviceData();
            if (selectedMarkers.length > 0) {
              const group = new L.featureGroup(selectedMarkers); // eslint-disable-line new-cap
              const bounds = group.getBounds();
              return Promise.resolve(bounds);
            }
          }

          const group = new L.featureGroup(this.markers); // eslint-disable-line new-cap
          const bounds = group.getBounds();
          return Promise.resolve(bounds);
        });
    },

    refresh() {
      this.updateLayers();

      return locateDevices(this.devices, this.settings)
        .then((locations) => {
          // Handle no devices
          if (typeof locations === 'object' && Object.keys(locations).length === 0) {
            this.$emit('render-no-devices');
            this.clear();
            return Promise.reject(new Error('No devices to dsisplay'));
          }

          const render = this.settings.useHeatmap ? this.renderHeatmap : this.renderMarkers;
          return render(locations);
        }).then((bounds) => {
          if (bounds === undefined || bounds.isValid() === false) {
            return Promise.resolve();
          }

          // Only re-center the map if devices change and we have not got locked bounds
          if (this.parsedLockedBounds === null
            && this.lastDevices !== JSON.stringify(this.devices)) {
            this.lastDevices = JSON.stringify(this.devices);
            this.fitBounds(bounds);
          }

          this.$emit('render-complete');
          return Promise.resolve();
        }).catch(() => Promise.resolve()); // Errors can be ignored, we just wanted to escape
    },

    draw(elements = []) {
      // Start by clearing the layer
      this.drawingLayer.clearLayers();

      elements.forEach((el) => {
        if (el.type === 'polyline' && Array.isArray(el.points)) {
          const polyline = L.polyline(el.points, { color: '#8a8a8a' });
          polyline.addTo(this.drawingLayer);

          if (el.decorator === 'arrow') {
            // const symbol = L.Symbol.dash({ pixelSize: 10 });
            const decorator = {
              patterns: [
                {
                  offset: 30,
                  repeat: '25%',
                  symbol: L.Symbol.arrowHead({
                    pixelSize: 10,
                    polygon: false,
                    pathOptions: { stroke: true, color: '#8a8a8a' },
                  }),
                },
              ],
            };

            L.polylineDecorator(polyline, decorator).addTo(this.drawingLayer);
          }
        } else if (el.type === 'circle' && el.latLng) {
          const colour = el.colour || '#F14242';
          const radius = el.radius || 4;
          const circle = L.circleMarker(el.latLng, { radius, color: colour, fillOpacity: 1 });

          // Draw with a slight delay to help layering
          setTimeout(() => {
            circle.addTo(this.drawingLayer);
          }, 100);
        }
      });
    },

    async showRadiusIndicator(device, radius = 10000) {
      this.annotationLayer.clearLayers();
      if (!device) return;

      const { latitude, longitude } = await locateDevice(device, this.settings);
      this.radiusIndicator = L.circle([latitude, longitude], { radius });
      this.radiusIndicator.addTo(this.annotationLayer);
    },

    async addAnnotation(annotation) {
      const { type, location, fitBounds } = annotation;
      let layer;
      if (type === 'circle') {
        const { radius } = annotation;
        const point = location || this.map.getCenter();
        layer = L.circle(point, { radius });
      }

      layer.addTo(this.annotationLayer);

      if (fitBounds && typeof layer.getBounds === 'function') {
        this.fitBounds(layer.getBounds());
      }
    },

    async clearAnnotations() {
      this.annotationLayer.clearLayers();
    },

    clear() {
      if (!this.$el || !this.$refs.container) return;
      if (this.heatmap) {
        this.heatmap.setLatLngs([]);
      }

      // Sometimes clearLayers throws errors because I can't get a check deep enough to ensure the
      // map still exists when the calls to leaflet are done. Now it will just catch the error.
      try {
        if (this.cluster) {
          this.markers.forEach((m) => {
            m.off('click');
          });
          if (this.map.hasLayer(this.cluster) && this.cluster.getLayers().length > 0) {
            this.cluster.clearLayers();
          }
        }
        if (this.drawingLayer) {
          this.drawingLayer.clearLayers();
        }
        if (this.annotationLayer) {
          this.annotationLayer.clearLayers();
        }
      // Thrown error does not cause any issues because the map is gone, so it is safe to ignore
      } catch (e) {} // eslint-disable-line no-empty
    },

    centerOnMarkers() {
      let bounds = null;
      if (this.settings.useHeatmap) {
        bounds = new L.latLngBounds(this.heatmap._latlngs); // eslint-disable-line new-cap, no-underscore-dangle, max-len
      } else {
        const group = new L.featureGroup(this.markers); // eslint-disable-line new-cap
        bounds = group.getBounds();
      }

      if (!bounds || !bounds.isValid()) {
        return Promise.reject();
      }

      this.fitBounds(bounds);
      return Promise.resolve();
    },

    centerOnLatLngs(latLngs = []) {
      const bounds = new L.latLngBounds(latLngs); // eslint-disable-line new-cap
      if (!bounds || !bounds.isValid()) {
        return;
      }

      this.fitBounds(bounds);
    },

    updateSelectedDeviceMarkers() {
      this.markers.forEach((m) => {
        if (m.options.isSelected) {
          m.setIcon(m.selectedIcon);
        } else {
          m.setIcon(m.originalIcon);
        }
      });
    },

    pushDeviceSelectionChange() {
      const selectionArray = [];
      this.markers.forEach((m) => {
        if (m.options.isSelected) {
          selectionArray.push(m.options.id);
        }
      });
      this.$emit('selection-changed', selectionArray);
    },

    changedLayer(layer) {
      this.$emit('layer-changed', layer.name);
    },

    changedOverlays() {
      this.$emit('overlays-changed', this.layerControl.getActiveOverlayNames());
    },

    changedBounds() {
      this.$emit('bounds-changed', this.map.getBounds());
    },

    fitBounds(bounds) {
      const fitTo = this.parsedLockedBounds ? this.parsedLockedBounds : bounds;
      if (!fitTo || !fitTo.isValid()) {
        return;
      }
      this.map.fitBounds(fitTo, {
        maxZoom: FIT_BOUNDS_MAX_ZOOM,
      });
    },

    handleResize() {
      if (this.map && this.$refs.container) {
        this.map.invalidateSize();
      }
    },
  },
};
